import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import {
  getAuthToken,
  getRefreshToken,
  setAuthToken,
  setRefreshToken,
  setTokenExpiry,
  clearAuthData,
  isTokenExpired
} from './auth';

const API_URL = import.meta.env.VITE_API_URL || 'https://new-api.cloudmedia.com.tr/api';

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true
});

// Token yenileme durumu
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

// Kuyruktaki istekleri işle
const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Token yenileme fonksiyonu
const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await axios.post(`${API_URL}/auth/refresh-token`, {
    refreshToken
  });

  const { token, refreshToken: newRefreshToken, expiresIn } = response.data.data;

  // Yeni token'ları kaydet
  setAuthToken(token);
  setRefreshToken(newRefreshToken);
  if (expiresIn) {
    setTokenExpiry(expiresIn);
  }

  return token;
};

// Request interceptor - Her request'e token ekle
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    let token = getAuthToken();

    // Token varsa ve süresi dolmak üzereyse yenile
    if (token && isTokenExpired() && !config.url?.includes('/auth/refresh-token')) {
      try {
        if (!isRefreshing) {
          isRefreshing = true;
          token = await refreshAccessToken();
          isRefreshing = false;
          processQueue(null, token);
        } else {
          // Zaten yenileme yapılıyorsa kuyruğa ekle
          token = await new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          });
        }
      } catch (error) {
        isRefreshing = false;
        processQueue(error as Error, null);
        // Refresh başarısız, login'e yönlendir
        clearAuthData();
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - 401 hatalarında token yenile
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // 401 hatası ve henüz retry yapılmadıysa
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Refresh token endpoint'ine yapılan istekleri atla
      if (originalRequest.url?.includes('/auth/refresh-token')) {
        clearAuthData();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Zaten yenileme yapılıyorsa kuyruğa ekle
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(axiosInstance(originalRequest));
            },
            reject: (err: Error) => {
              reject(err);
            }
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        isRefreshing = false;
        processQueue(null, newToken);

        // Orijinal isteği yeni token ile tekrar gönder
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError as Error, null);

        // Refresh başarısız, login'e yönlendir
        const currentPath = window.location.pathname;
        if (currentPath !== '/login') {
          clearAuthData();
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    // Diğer hatalar için sadece log
    if (error.response?.status !== 401) {
      console.error('Response error:', {
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
    }

    return Promise.reject(error);
  }
);

export { axiosInstance };
export default axiosInstance;
